import * as tiptap from '@tiptap/core'
import { Color } from '@tiptap/extension-color'
import Image from '@tiptap/extension-image'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import * as styles from '#blocks/stylesheets/inline'
import * as html from '#editors/html'
import { FontSize } from '#editors/modules/font-size.extension'
import { Index } from '#editors/modules/index.extension'
import { LineHeight } from '#editors/modules/line-height.extension'
import { Table } from '#editors/modules/table.extend'

export function register() {
  customElements.define('blocks-tiptap', BlocksEditor)
}

export class BlocksEditor extends HTMLElement {
  #pastContents: Set<string>
  #shadow: ShadowRoot
  #container: HTMLElement
  #editor?: tiptap.Editor
  #content: string
  #clearUpdateDebouncer: () => void

  static get observedAttributes() {
    return ['editable']
  }

  constructor() {
    super()
    this.#pastContents = new Set()
    this.#content = ''
    this.#shadow = this.attachShadow({ mode: 'open' })
    this.#container = html.div([], [])
    this.#container.setAttribute('class', 'blocks-editor')
    this.#shadow.appendChild(this.#container)
    this.#appendStyles()
    this.#clearUpdateDebouncer = () => {}
  }

  set content(value: string) {
    if (this.#pastContents.has(value)) {
      this.#pastContents.delete(value)
    } else {
      this.#content = value
      this.#editor?.commands.setContent(value, false, {
        preserveWhitespace: 'full',
      })
    }
  }

  connectedCallback() {
    const types = ['heading', 'paragraph', 'textStyle']
    const editable = this.#readEditableState()
    this.#editor = new tiptap.Editor({
      element: this.#container,
      extensions: [
        Color,
        FontSize.configure({ types }),
        Image,
        Index,
        LineHeight.configure({ types }),
        StarterKit.configure({ dropcursor: false, horizontalRule: false }),
        Table.configure({ resizable: true }),
        TableCell,
        TableHeader,
        TableRow,
        TextAlign.configure({ types }),
        TextStyle,
        Underline,
      ],
      content: this.#content,
      injectCSS: false,
      editable,
      parseOptions: { preserveWhitespace: 'full' },
      onBeforeCreate: () => this.#dispatch('beforecreate'),
      onBlur: p => this.#dispatch('blur', { transaction: p.transaction }),
      onContentError: () => this.#dispatch('contenterror'),
      onCreate: () => this.#dispatch('create'),
      // onDestroy: () => this.#dispatch('destroy'),
      onDrop: () => this.#dispatch('drop'),
      onFocus: p => this.#dispatch('focus', { transaction: p.transaction }),
      onPaste: () => this.#dispatch('paste'),
      onSelectionUpdate: p =>
        this.#dispatch('selectionupdate', { transaction: p.transaction }),
      onTransaction: p =>
        this.#dispatch('transaction', { transaction: p.transaction }),
      onUpdate: p => {
        this.#dispatch('update', { transaction: p.transaction })
        this.#clearUpdateDebouncer()
        const id = setTimeout(() => this.#saveContent(), 500)
        this.#clearUpdateDebouncer = () => clearTimeout(id)
      },
      editorProps: {
        transformPastedText(text) {
          return text
        },
        transformPastedHTML(text) {
          try {
            const parsed = new DOMParser().parseFromString(text, 'text/html')
            if (!parsed.body) return text
            removeClass(parsed.body)
            return parsed.body.innerHTML
          } catch (_) {
            return text
          }
        },
      },
    })
    this.#dispatch('connected')
  }

  attributeChangedCallback(
    _name: 'editable',
    _previous: string,
    _next: string
  ) {
    const editable = this.#readEditableState()
    this.#editor?.setEditable(editable)
  }

  disconnectedCallback() {
    this.#saveContent()
    this.#editor?.destroy()
  }

  getContent() {
    return this.#editor?.getHTML()
  }

  async #appendStyles() {
    const stylesheet = new CSSStyleSheet()
    await stylesheet.replace(styles.main)
    this.#shadow.adoptedStyleSheets.push(stylesheet)
    if (import.meta.env.VITE_EDITOR_DEBUG === 'true') {
      const stylesheet = new CSSStyleSheet()
      await stylesheet.replace(styles.debug)
      this.#shadow.adoptedStyleSheets.push(stylesheet)
    }
  }

  #dispatch(name: string, details: object = {}) {
    const content = { detail: { editor: this.#editor, ...details } }
    const event = new CustomEvent(name, content)
    this.dispatchEvent(event)
  }

  #readEditableState() {
    const attribute = this.attributes.getNamedItem('editable')
    const value = attribute?.value?.toLowerCase()
    if (value === 'false') return false
    return true
  }

  #saveContent() {
    const content = this.#editor?.getHTML() ?? ''
    if (this.#content === content) return
    this.#pastContents.add(content)
    this.#content = content
    this.#dispatch('updated', { content })
  }
}

function removeClass(node: Element) {
  node.removeAttribute('class')
  node.removeAttribute('style')
  for (const child of node.children) removeClass(child)
}
